import { pageData, pipelineRoot, PipelineReasonData, PipelineStageData } from "./request";

export const REJECT = "__reject";

let current: HTMLElement | null = null;
let cancelCurrent: (() => void) | null = null;

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function closePopover(cancelled = true): void {
  const cancel = cancelCurrent;
  current?.remove();
  current = null;
  cancelCurrent = null;
  if (cancelled && cancel) cancel();
}

function place(pop: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const width = pop.offsetWidth || 230;
  const height = pop.offsetHeight || 200;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = rect.bottom + 6;
  const top = below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 6) : below;
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
}

function open(anchor: HTMLElement, html: string, onPick: (value: string) => void, onCancel?: () => void): void {
  closePopover();
  const root = pipelineRoot() || document.body;
  const pop = document.createElement("div");
  pop.className = "pl-pop";
  pop.setAttribute("role", "menu");
  pop.innerHTML = html;
  root.appendChild(pop);
  place(pop, anchor);
  current = pop;
  cancelCurrent = onCancel || null;
  pop.addEventListener("click", (event) => {
    const option = (event.target as HTMLElement).closest<HTMLElement>("[data-pl-opt]");
    if (!option) return;
    event.stopPropagation();
    const value = option.dataset.plOpt!;
    closePopover(false);
    onPick(value);
  });
  pop.querySelector<HTMLElement>("[data-pl-opt]")?.focus();
}

export function openStagePicker(
  anchor: HTMLElement,
  currentStageId: string | null,
  onPick: (stageIdOrReject: string) => void,
  options: { stages?: PipelineStageData[]; allowReject?: boolean; onCancel?: () => void } = {},
): void {
  const stages = options.stages || pageData().stages;
  const rows = stages
    .map(
      (stage) =>
        `<button type="button" class="pl-pop-opt${stage.id === currentStageId ? " is-current" : ""}" data-pl-opt="${stage.id}" role="menuitem">` +
        `<span class="pl-dot" style="background:${escapeHtml(stage.color)}"></span>${escapeHtml(stage.name)}</button>`,
    )
    .join("");
  const reject =
    options.allowReject === false
      ? ""
      : `<div class="pl-pop-div"></div><button type="button" class="pl-pop-opt is-exit" data-pl-opt="${REJECT}" role="menuitem">Reject…</button>`;
  open(anchor, `<div class="pl-pop-title">Move to stage</div>${rows}${reject}`, onPick, options.onCancel);
}

export function openReasonPopover(
  anchor: HTMLElement,
  title: string,
  onPick: (reasonId: string) => void,
  options: { reasons?: PipelineReasonData[]; onCancel?: () => void } = {},
): void {
  const reasons = options.reasons || pageData().reasons;
  const rows = reasons.length
    ? reasons
        .map((r) => `<button type="button" class="pl-pop-opt" data-pl-opt="${r.id}" role="menuitem">${escapeHtml(r.label)}</button>`)
        .join("")
    : `<div class="pl-pop-empty">Add a rejection reason in Edit stages first.</div>`;
  open(anchor, `<div class="pl-pop-title">${escapeHtml(title)}</div>${rows}`, onPick, options.onCancel);
}

let bound = false;

export function initPopovers(): void {
  if (bound) return;
  bound = true;
  document.addEventListener("mousedown", (event) => {
    if (current && !current.contains(event.target as Node)) closePopover();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && current) closePopover();
  });
  document.addEventListener("turbo:before-cache", () => closePopover());
}
